import React, { useState, useEffect } from 'react';
import { FileText, Loader2, ChevronRight, Download, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { HorizontalScroll, ScrollCard } from '../components/HorizontalScroll';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Credit Disputes & Credit Reporting': '💳',
  'Business Credit & Business Setup': '🏢',
  'Court Motions & Legal Filings': '⚖️',
  'Affidavits & Sworn Statements': '📜',
  'Trust & Estate Planning': '🏛️',
  'Legal Notices & Demand Letters': '📨',
  'Landlord-Tenant Disputes': '🏠',
  'Lawsuits & Civil Complaints': '⚔️',
};

export default function TrendingTemplates() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trending`)
      .then(res => { setGuides(res.data.products || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleQuickBuy = async (productId) => {
    try {
      setPurchasing(productId);
      const session = await createCheckoutSession(productId);
      if (session.url) window.location.href = session.url;
    } catch { alert('Unable to process purchase.'); }
    finally { setPurchasing(null); }
  };

  // Group by category
  const grouped = {};
  guides.forEach(g => {
    const cat = g.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(g);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-700 via-orange-600 to-amber-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <TrendingUp className="w-8 h-8" />
            <span className="text-3xl">📈</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Trending Templates</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            The most popular legal document templates across all categories — hand-picked and ready to download.
          </p>
          <p className="text-sm text-white/70 mt-2">{guides.length} trending guides from {Object.keys(grouped).length} categories</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="space-y-12">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, catGuides]) => (
              <div key={category} data-testid={`trending-category-${category}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{CATEGORY_ICONS[category] || '📄'}</span>
                  <h2 className="text-xl font-bold text-gray-900">{category}</h2>
                  <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">{catGuides.length} trending</span>
                </div>
                <HorizontalScroll>
                  {catGuides.map(product => (
                    <ScrollCard key={product.id} width="w-52 sm:w-60">
                      <div
                        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full"
                        data-testid={`trending-product-${product.id}`}
                      >
                        <div className="relative">
                          <BookMockup productId={product.id} title={product.title} size="card" badge={getBadge(product)} />
                        </div>
                        <div className="p-3">
                          <h3 className="text-xs font-bold text-gray-900 mb-2 line-clamp-2 text-center">{product.title}</h3>
                          <div className="flex items-center justify-between">
                            <a
                              href={`/guide/${product.id}`}
                              className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors"
                              data-testid={`trending-view-${product.id}`}
                            >
                              View Details
                            </a>
                            <button
                              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                              onClick={() => window.location.href = `/guide/${product.id}`}
                              disabled={purchasing === product.id}
                              data-testid={`trending-buy-${product.id}`}
                            >
                              {purchasing === product.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </ScrollCard>
                  ))}
                </HorizontalScroll>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
